import { useAddBankName, useGetBankList } from '@nxsys/hooks/api/bank';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { showMessage } from '@nxsys/utils/message';
import { Button, Divider, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledAppSelect } from '../AppSelect/index.styled';

type OptionType = {
  label: string;
  value: string;
};

type BankSelectInputProps = {
  value?: OptionType | null;
  onChange?: (value: OptionType | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

const BankSelectInput = ({
  value,
  onChange,
  placeholder,
  disabled,
  className,
}: BankSelectInputProps) => {
  const { messages } = useTypedIntl();
  const {
    run: fetchBankList,
    value: bankListValue,
    loading: fetching,
  } = useGetBankList();
  const { run: addBankName, loading: adding } = useAddBankName();

  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<OptionType[]>([]);

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        fetchBankList({ page: 0, size: 20, search: term });
      }, 300),
    [],
  );

  useEffect(() => {
    if (bankListValue?.data) {
      setOptions(
        bankListValue.data.map((bank) => ({
          label: bank.bankName,
          value: bank.bankName,
        })),
      );
    }
  }, [bankListValue]);

  useEffect(() => {
    fetchBankList({ page: 0, size: 20 });
    return () => {
      debouncedSearch.cancel();
    };
  }, []);

  const handleSearch = useCallback(
    (val: string) => {
      setSearchValue(val);
      debouncedSearch(val);
    },
    [debouncedSearch],
  );

  const handleAddBank = async () => {
    const trimmed = searchValue.trim();
    if (!trimmed) return;

    try {
      const result = await addBankName({ bankName: trimmed });
      if (result?.data) {
        const newOption = {
          label: result.data.bankName,
          value: result.data.bankName,
        };
        setSearchValue('');
        onChange?.(newOption);
        fetchBankList({ page: 0, size: 20 });
        showMessage({
          type: 'success',
          content: messages['common.updateSuccess'],
        });
      }
    } catch {
      // Error handled by useAddBankName hook
    }
  };

  const showAddButton =
    searchValue.trim() &&
    !options.some(
      (opt) => opt.label.toLowerCase() === searchValue.trim().toLowerCase(),
    );

  return (
    <StyledAppSelect
      labelInValue
      showSearch
      allowClear
      size="large"
      value={value}
      searchValue={searchValue}
      onSearch={handleSearch}
      filterOption={false}
      options={options}
      onChange={(_: any, option: any) => onChange?.(option ?? null)}
      onClear={() => onChange?.(null)}
      placeholder={placeholder ?? messages['form.select']}
      disabled={disabled}
      className={className}
      loading={fetching}
      notFoundContent={
        fetching ? <Spin size="small" /> : messages['common.noData']
      }
      popupRender={(menu: React.ReactNode) => (
        <>
          {menu}
          {showAddButton && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ padding: '4px 8px' }}>
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={handleAddBank}
                  loading={adding}
                  block
                >
                  {`${messages['common.add']} "${searchValue.trim()}"`}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    />
  );
};

export default BankSelectInput;
